import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGES, TRANSLATIONS } from './translations';

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key
});

const STORAGE_KEY = 'shatranj_language';
const RTL_LANGS = new Set(['ar', 'ur']);

const interpolate = (template, params = {}) => {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? `{${key}}`));
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const urlParam = new URLSearchParams(window.location.search).get('lang');
    if (urlParam && TRANSLATIONS[urlParam]) return urlParam;
    const stored = localStorage.getItem(STORAGE_KEY);
    return TRANSLATIONS[stored] ? stored : 'en';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = RTL_LANGS.has(language) ? 'rtl' : 'ltr';

    // Keep ?lang= in sync so hreflang URLs are crawlable
    const url = new URL(window.location.href);
    if (language === 'en') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', language);
    }
    window.history.replaceState({}, '', url.toString());
  }, [language]);

  const value = useMemo(() => {
    const t = (key, params = {}) => {
      const langTable = TRANSLATIONS[language] || TRANSLATIONS.en;
      const raw = langTable[key] ?? TRANSLATIONS.en[key] ?? key;
      return interpolate(raw, params);
    };
    return { language, setLanguage, t, languages: LANGUAGES };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
